import { useEffect, useState } from "react";
import axios from "axios";
import { X, Calculator, ArrowRight } from "lucide-react";

const CONFIGS = ["2BHK", "3BHK", "4+BHK"];
const AREAS = ["1000-1400 sqft", "1500-2000 sqft", ">2000 sqft"];
const TIERS = ["Basic", "Premium", "Luxury", "Ultra Luxury"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUR_BHK_MESSAGE = "We will contact you soon to discuss the requirement.";

const PRICES = {
  "2BHK": {
    "1000-1400 sqft": { Basic: "₹5-8 Lakhs", Premium: "₹9-12 Lakhs", Luxury: "₹13-15 Lakhs", "Ultra Luxury": "₹18 Lakhs+" },
    "1500-2000 sqft": { Basic: "₹9-12 Lakhs", Premium: "₹13-15 Lakhs", Luxury: "₹18-20 Lakhs", "Ultra Luxury": "₹24 Lakhs+" },
    ">2000 sqft": { Basic: "₹15-18 Lakhs", Premium: "₹18-20 Lakhs", Luxury: "₹22-25 Lakhs", "Ultra Luxury": "₹25 Lakhs+" },
  },
  "3BHK": {
    "1000-1400 sqft": { Basic: "₹10-12 Lakhs", Premium: "₹12-15 Lakhs", Luxury: "₹16-18 Lakhs", "Ultra Luxury": "₹20 Lakhs+" },
    "1500-2000 sqft": { Basic: "₹15-18 Lakhs", Premium: "₹18-20 Lakhs", Luxury: "₹20-30 Lakhs", "Ultra Luxury": "₹30 Lakhs+" },
    ">2000 sqft": { Basic: "₹18-20 Lakhs", Premium: "₹22-26 Lakhs", Luxury: "₹28-34 Lakhs", "Ultra Luxury": "₹35 Lakhs+" },
  },
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Chip = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-4 py-2.5 text-sm font-medium border transition-colors ${
      active
        ? "bg-[#0D2121] text-[#FAF6F0] border-[#0D2121]"
        : "bg-[#F4EDE2] text-[#0D2121] border-[#0D2121]/15 hover:border-[#E2A435]"
    }`}
  >
    {children}
  </button>
);

export const CostCalculator = () => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState("2BHK");
  const [area, setArea] = useState(AREAS[0]);
  const [tier, setTier] = useState("Basic");
  const [step, setStep] = useState("select"); // select -> lead -> result
  const [lead, setLead] = useState({ name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (!open) return null;

  const isFourBhk = config === "4+BHK";
  const estimate = isFourBhk ? "" : PRICES[config][area][tier];

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/calculator-leads`, {
        name: lead.name,
        phone: lead.phone,
        config,
        area,
        tier,
        estimate: estimate || "To be discussed (4+BHK)",
      });
    } catch (err) {
      console.error("Lead capture failed", err);
    } finally {
      setSubmitting(false);
      setStep("result");
    }
  };

  const getQuote = () => {
    const message = isFourBhk
      ? `Cost calculator: ${config} | ${area} | ${tier} interiors. Please contact me to discuss the requirement.`
      : `Cost calculator estimate: ${config} | ${area} | ${tier} interiors -> ${estimate}. I'd like an exact quote.`;
    window.dispatchEvent(new CustomEvent("griham:prefill", { detail: message }));
    setOpen(false);
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const labelClass = "block text-xs uppercase tracking-wider text-[#7A6F66] mb-2 font-semibold";
  const inputClass =
    "w-full px-4 py-3 bg-[#F4EDE2] border border-[#0D2121]/20 text-sm text-[#0D2121] focus:outline-none focus:border-[#E2A435]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0D2121]/80 backdrop-blur-sm px-4" data-testid="cost-calculator-modal">
      <div className="bg-[#FAF6F0] text-[#0D2121] max-w-lg w-full p-8 rounded-sm shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-[#c88c2a] text-xs uppercase tracking-widest font-semibold mb-2">
              <Calculator size={14} />
              Instant Estimate
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
              What will your interiors cost?
            </h3>
            <p className="text-xs text-[#7A6F66] mt-1">Three quick picks — see your indicative budget range.</p>
          </div>
          <button
            type="button"
            aria-label="Close cost calculator"
            data-testid="calculator-close-btn"
            onClick={() => setOpen(false)}
            className="p-1 text-[#7A6F66] hover:text-[#0D2121] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {step === "select" && (
          <div className="space-y-6">
            <div>
              <span className={labelClass}>Configuration</span>
              <div className="flex flex-wrap gap-2">
                {CONFIGS.map((c) => (
                  <Chip key={c} active={config === c} onClick={() => setConfig(c)} testId={`calc-config-${slug(c)}`}>
                    {c}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <span className={labelClass}>Sqft Area</span>
              <div className="flex flex-wrap gap-2">
                {AREAS.map((a) => (
                  <Chip key={a} active={area === a} onClick={() => setArea(a)} testId={`calc-area-${slug(a)}`}>
                    {a}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <span className={labelClass}>Type of Interiors</span>
              <div className="flex flex-wrap gap-2">
                {TIERS.map((t) => (
                  <Chip key={t} active={tier === t} onClick={() => setTier(t)} testId={`calc-tier-${slug(t)}`}>
                    {t}
                  </Chip>
                ))}
              </div>
            </div>

            <button
              type="button"
              data-testid="calc-view-estimate-btn"
              onClick={() => setStep("lead")}
              className="w-full py-4 bg-[#E2A435] hover:bg-[#c88c2a] text-[#0D2121] font-semibold text-sm uppercase tracking-wider transition-colors"
            >
              View My Estimate
            </button>
          </div>
        )}

        {step === "lead" && (
          <form className="space-y-4" onSubmit={submitLead} data-testid="calc-lead-form">
            <p className="text-sm text-[#7A6F66] leading-relaxed">
              Almost there — share your details to view your estimate. We will only call you about your project.
            </p>
            <div>
              <label className={labelClass} htmlFor="calc-lead-name">Your Name</label>
              <input
                id="calc-lead-name"
                type="text"
                required
                value={lead.name}
                onChange={(e) => setLead({ ...lead, name: e.target.value })}
                placeholder="e.g. Aditi Sharma"
                data-testid="calc-lead-name-input"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="calc-lead-phone">Phone Number</label>
              <input
                id="calc-lead-phone"
                type="tel"
                required
                minLength={10}
                value={lead.phone}
                onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                placeholder="e.g. +91 98765 43210"
                data-testid="calc-lead-phone-input"
                className={inputClass}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="calc-lead-submit-btn"
                className="flex-1 py-4 bg-[#E2A435] hover:bg-[#c88c2a] disabled:opacity-60 text-[#0D2121] font-semibold text-sm uppercase tracking-wider transition-colors"
              >
                {submitting ? "Please wait..." : "Show My Estimate"}
              </button>
              <button
                type="button"
                data-testid="calc-lead-back-btn"
                onClick={() => setStep("select")}
                className="px-6 py-4 border border-[#0D2121]/25 text-[#0D2121] text-sm font-semibold uppercase tracking-wider hover:bg-[#EADBC8] transition-colors"
              >
                Back
              </button>
            </div>
          </form>
        )}

        {step === "result" && (
          <div className="space-y-6">
            <div className="bg-[#0D2121] text-[#FAF6F0] p-6 text-center space-y-3" data-testid="calc-result-panel">
              <p className="text-xs uppercase tracking-widest text-[#EADBC8]/70">
                {config} &middot; {area} &middot; {tier}
              </p>
              {isFourBhk ? (
                <div className="font-display text-2xl sm:text-3xl text-[#E2A435] leading-snug" data-testid="calc-result-text">
                  {FOUR_BHK_MESSAGE}
                </div>
              ) : (
                <div className="font-display text-4xl sm:text-5xl text-[#E2A435]" data-testid="calc-result-text">
                  {estimate}
                </div>
              )}
              <p className="text-[11px] text-[#EADBC8]/60 leading-relaxed">
                {isFourBhk
                  ? "Larger homes need a detailed walkthrough before we can quote honestly."
                  : "Indicative range for full-home interiors. Your exact quote depends on materials, site condition, and scope."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="calc-get-quote-btn"
                onClick={getQuote}
                className="flex-1 inline-flex items-center justify-center gap-2 py-4 bg-[#E2A435] hover:bg-[#c88c2a] text-[#0D2121] font-semibold text-sm uppercase tracking-wider transition-colors"
              >
                Get Exact Quote
                <ArrowRight size={16} />
              </button>
              <button
                type="button"
                data-testid="calc-recalculate-btn"
                onClick={() => setStep("select")}
                className="px-6 py-4 border border-[#0D2121]/25 text-[#0D2121] text-sm font-semibold uppercase tracking-wider hover:bg-[#EADBC8] transition-colors"
              >
                Recalculate
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
